<template>
  <div>
    <div class="svc-metrics">
      <!-- Tarjeta 1: Total del mes -->
      <button class="svc-metric-card" style="border-top-color: #3b82f6" @click="$emit('scroll-to-list')">
        <span class="svc-metric-card__label">Total del mes</span>
        <span class="svc-metric-card__value" :class="{ 'svc-metric-card__value--empty': metrics.isEmpty }">
          {{ metrics.isEmpty ? '$0' : formatCurrency(metrics.totalAmount) }}
        </span>

        <template v-if="metrics.isEmpty">
          <span class="svc-metric-card__sub svc-metric-card__sub--muted">
            Sin servicios registrados · Comienza agregando el primero
          </span>
          <button class="svc-metric-card__link" @click.stop="$emit('register-service')">Registrar servicio</button>
        </template>
        <template v-else>
          <span class="svc-metric-card__sub">
            <span class="svc-metric-card__green">{{ metrics.distributedCount }} distribuidos</span>
            ·
            <span :class="metrics.pendingRegisterCount > 0 ? 'svc-metric-card__amber' : 'svc-metric-card__gray'">
              {{ metrics.conceptCount - metrics.distributedCount }} pendientes
            </span>
          </span>
          <span class="svc-metric-card__sub svc-metric-card__sub--muted">{{ metrics.conceptCount }} conceptos registrados</span>
        </template>
      </button>

      <!-- Tarjeta 2: Costo por m² -->
      <div class="svc-metric-card-wrap">
        <button class="svc-metric-card" :style="{ borderTopColor: costColorHex }" @click="toggleHistory">
          <span class="svc-metric-card__label">Costo por m²</span>
          <span class="svc-metric-card__sublabel">mes actual</span>
          <span class="svc-metric-card__value" :style="{ color: costColorHex }">
            <span class="svc-metric-card__value-affix">$</span>{{ metrics.costPerM2 !== null ? metrics.costPerM2.toFixed(2) : '—' }}<span class="svc-metric-card__value-affix">/m²</span>
          </span>

          <span v-if="metrics.costPerM2 === null && metrics.areaHint" class="svc-metric-card__sub svc-metric-card__sub--muted svc-metric-card__hint">{{ metrics.areaHint }}</span>
          <span v-else-if="metrics.areaSource === 'PLOTS'" class="svc-metric-card__sub svc-metric-card__sub--muted">Con la suma de m² de las naves</span>

          <span v-if="metrics.costPerM2Diff !== null" class="svc-metric-card__sub" :class="metrics.costPerM2Diff <= 0 ? 'svc-metric-card__green' : 'svc-metric-card__amber'">
            {{ metrics.costPerM2Diff <= 0 ? '▼' : '▲' }} {{ metrics.costPerM2Diff <= 0 ? '−' : '+' }}${{ Math.abs(metrics.costPerM2Diff).toFixed(2) }} vs {{ metrics.costPerM2PrevLabel }}
          </span>
          <span v-if="metrics.avg6m !== null" class="svc-metric-card__sub svc-metric-card__sub--muted">Promedio 6m: ${{ metrics.avg6m.toFixed(2) }}/m²</span>
          <span v-if="metrics.unusualVariation" class="svc-metric-card__sub svc-metric-card__red">
            <span class="mdi mdi-alert-outline"></span> Variación inusual · Revisa los conceptos
          </span>
        </button>

        <div v-if="showHistory" class="svc-metric-history">
          <div v-if="loadingHistory" class="svc-metric-history__loading">Cargando...</div>
          <table v-else class="svc-metric-history__table">
            <tr v-for="h in history" :key="h.label">
              <td>{{ h.label }}</td>
              <td>{{ h.costPerM2 !== null ? `$${h.costPerM2.toFixed(2)}` : '—' }}</td>
            </tr>
          </table>
        </div>
      </div>

      <!-- Tarjeta 3: Estatus de distribución -->
      <button class="svc-metric-card" :style="{ borderTopColor: distributionColorHex }" @click="$emit('scroll-to-distribution')">
        <span class="svc-metric-card__label">Distribución</span>
        <span class="svc-metric-card__sublabel">{{ periodLabel }}</span>

        <template v-if="metrics.distribution.periodStatus === 'CLOSED'">
          <div class="svc-metric-card__progress-bg"><div class="svc-metric-card__progress-fill svc-metric-card__progress-fill--green" style="width: 100%"></div></div>
          <span class="svc-metric-card__sub svc-metric-card__green" style="font-weight: 500;">
            Cerrado el {{ formatDate(metrics.distribution.closedAt) }}
          </span>
          <span class="svc-metric-card__sub svc-metric-card__sub--muted">
            Cargos transferidos a cobranza · {{ metrics.distribution.tenantCount ?? 0 }} inquilinos
          </span>
        </template>
        <template v-else>
          <div class="svc-metric-card__progress-bg">
            <div class="svc-metric-card__progress-fill" :class="progressFillClass" :style="{ width: metrics.distribution.percent + '%' }"></div>
          </div>
          <span class="svc-metric-card__sub" :style="{ color: distributionColorHex, fontWeight: 500 }">
            {{ metrics.distribution.distributedCount }} de {{ metrics.distribution.totalCount }} servicios · {{ metrics.distribution.percent }}%
          </span>

          <span v-if="metrics.distribution.allDistributed" class="svc-metric-card__sub svc-metric-card__green">
            Todos los servicios distribuidos <span class="mdi mdi-check"></span>
          </span>
          <span v-else-if="metrics.distribution.pendingNames.length" class="svc-metric-card__sub svc-metric-card__amber">
            Pendientes: {{ pendingNamesLabel }}
          </span>

          <span v-if="metrics.distribution.allDistributed" class="svc-metric-card__sub" style="color: #3b82f6;">
            Listo para transferir a cobranza · Usa el botón superior
          </span>
        </template>
      </button>
    </div>

    <div v-if="isHistoricalMonth" class="svc-metrics__query-banner">
      Modo consulta · {{ periodLabel }} · {{ statusLabel }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { getCostPerM2History } from '@/services/services'

const props = defineProps({
  metrics: { type: Object, required: true },
  period: { type: String, required: true },
  parkId: { type: String, required: true },
})

defineEmits(['scroll-to-list', 'scroll-to-distribution', 'register-service'])

const periodLabel = computed(() => {
  const [y, m] = props.period.split('-').map(Number)
  const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
  return `${MONTHS[m - 1]} ${y}`
})

const isHistoricalMonth = computed(() => {
  const now = new Date()
  const currentValue = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  return props.period !== currentValue
})

const statusLabel = computed(() => {
  return { PENDING: 'Pendiente', IN_REVIEW: 'En revisión', DISTRIBUTED: 'Distribuido', CLOSED: 'Mes cerrado' }[props.metrics.distribution.periodStatus] || ''
})

const costColorHex = computed(() => ({ green: '#10b981', amber: '#f0a500', red: '#e84545' }[props.metrics.costPerM2Color] || '#1e3a5f'))

const distributionColorHex = computed(() => {
  const d = props.metrics.distribution
  if (d.totalCount === 0) return '#94a3b8'
  if (d.allDistributed) return '#10b981'
  return '#f0a500'
})

const progressFillClass = computed(() => {
  const d = props.metrics.distribution
  if (d.totalCount === 0) return 'svc-metric-card__progress-fill--gray'
  if (d.allDistributed) return 'svc-metric-card__progress-fill--green'
  return 'svc-metric-card__progress-fill--amber'
})

const pendingNamesLabel = computed(() => {
  const names = props.metrics.distribution.pendingNames
  if (names.length <= 2) return names.join(' · ')
  return `${names.slice(0, 2).join(' · ')} y ${names.length - 2} más`
})

const showHistory = ref(false)
const loadingHistory = ref(false)
const history = ref([])

async function toggleHistory() {
  showHistory.value = !showHistory.value
  if (showHistory.value && history.value.length === 0) {
    loadingHistory.value = true
    try {
      history.value = await getCostPerM2History(props.parkId, props.period)
    } finally {
      loadingHistory.value = false
    }
  }
}

function formatCurrency(n) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n)
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })
}
</script>

<style scoped>
.svc-metrics {
  display: flex;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  min-height: 80px;
}

.svc-metric-card-wrap { flex: 1; position: relative; }

.svc-metric-card {
  flex: 1;
  width: 100%;
  min-width: 0;
  background: var(--bg-surface-1);
  border: 1px solid var(--border-active);
  border-radius: 8px;
  border-top: 3px solid transparent;
  padding: 0.5rem 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}

.svc-metric-card__label { font-family: var(--font-body); font-size: 11px; font-weight: 500; color: #64748b; }
.svc-metric-card__sublabel { font-family: var(--font-body); font-size: 10px; color: #94a3b8; margin-top: -0.15rem; }

.svc-metric-card__value {
  font-family: var(--font-mono);
  font-weight: 700;
  font-size: 22px;
  color: #1e3a5f;
  line-height: 1.15;
}

.svc-metric-card__value--empty { color: #94a3b8; }
.svc-metric-card__value-affix { font-family: var(--font-body); font-size: 13px; color: #94a3b8; }

.svc-metric-card__sub {
  font-family: var(--font-body);
  font-size: 11px;
  color: var(--text-content);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.svc-metric-card__sub--muted { color: #94a3b8; font-size: 10px; }
.svc-metric-card__hint { -webkit-line-clamp: 4; line-height: 1.35; }
.svc-metric-card__green { color: #10b981; }
.svc-metric-card__amber { color: #d97706; }
.svc-metric-card__red { color: #dc2626; }
.svc-metric-card__gray { color: #94a3b8; }

.svc-metric-card__link {
  background: none; border: none; color: #3b82f6; font-family: var(--font-body); font-size: 11px; cursor: pointer; text-align: left; padding: 0;
}

.svc-metric-card__progress-bg { height: 8px; background: #e2e8f0; border-radius: 4px; overflow: hidden; margin: 0.2rem 0; }
.svc-metric-card__progress-fill { height: 100%; }
.svc-metric-card__progress-fill--green { background: #10b981; }
.svc-metric-card__progress-fill--amber { background: #f0a500; }
.svc-metric-card__progress-fill--gray { background: #94a3b8; }

.svc-metric-history {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  padding: 0.5rem;
  z-index: 100;
  max-height: 240px;
  overflow-y: auto;
}

.svc-metric-history__loading { font-family: var(--font-body); font-size: 12px; color: #94a3b8; text-align: center; padding: 0.5rem; }
.svc-metric-history__table { width: 100%; font-family: var(--font-body); font-size: 12px; border-collapse: collapse; }
.svc-metric-history__table td { padding: 0.3rem 0.4rem; border-bottom: 1px solid #f1f5f9; color: #1e3a5f; }
.svc-metric-history__table td:last-child { text-align: right; font-family: var(--font-mono); }

.svc-metrics__query-banner {
  height: 24px;
  display: flex;
  align-items: center;
  padding: 0 1.5rem;
  background: #eff6ff;
  font-family: var(--font-body);
  font-size: 11px;
  color: #3b82f6;
}
</style>